import React, { useEffect, useState } from 'react';
import ReactTable from 'react-table-6';
import 'react-table-6/react-table.css';
import { eventService, PostType } from '../../../services/eventService';
import { PanelRow } from '../../../components/panel/PanelRow';
import { Panel } from '../../../components/panel/Panel';
import { Spinner } from '../../../components/spinner/Spinner';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  setLoading,
  setPageIndex,
  setPageSize,
  setSearchTerm,
  setSortDirection,
  setSortedColumnName,
  setTotalPages,
  setEvents,
  useAppStateEventsTableSelector,
} from './EventsListSlice';
import { openMessageModal } from '../../../components/statusModal/messageModalActionCreators';
import { MessageModal } from '../../../components/statusModal/MessageModal';
import { Button, Input } from 'semantic-ui-react';
import styles from './EventsList.module.scss';

export const EventsList = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    events,
    totalPages,
    pageIndex,
    pageSize,
    sortedColumnName,
    sortDirection,
    searchTerm,
    loading,
  } = useAppStateEventsTableSelector((state) => state);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteEventId, setDeleteEventId] = useState('');
  const [isLoadingPageForFirstTime, setIsLoadingPageForFirstTime] = useState(true);
  const [postType, setPostType] = useState<PostType>('news');
  const [isReindexing, setIsReindexing] = useState(false);

  useEffect(() => {
    console.log('[DIAG] EventsList component mounted - /admin/content page loaded');
  }, []);

  const contentTypeLabel = postType === 'event' ? 'Event' : 'Article';
  const contentTypeLabelPlural = postType === 'event' ? 'Events' : 'Articles';

  const handleDeleteEvent = async (id: string) => {
    try {
      dispatch(setLoading(true));
      await eventService.deleteEvent(id);
      setTimeout(() => {
        dispatch(openMessageModal(`${contentTypeLabel} deleted successfully`, 'success'));
        fetchData(pageIndex, pageSize, sortedColumnName, sortDirection, searchTerm, postType);
      }, 500);
    } catch (err) {
      dispatch(openMessageModal(`Failed to delete ${contentTypeLabel.toLowerCase()}`, 'error'));
    } finally {
      setDeleteModalOpen(false);
      dispatch(setLoading(false));
    }
  };

  const handleEditEvent = (id: string) => {
    history.push(`/admin/content/edit/${id}`);
  };

  const handleCreateEvent = () => {
    history.push(`/admin/content/create?type=${postType}`);
  };

  const handleReindex = async () => {
    setIsReindexing(true);
    try {
      await eventService.reindexSearchIndex();
      dispatch(openMessageModal('Search index rebuilt successfully', 'success'));
    } catch (error) {
      console.error('Failed to rebuild search index:', error);
      dispatch(openMessageModal('Failed to rebuild search index', 'error'));
    } finally {
      setIsReindexing(false);
    }
  };

  const columns: Array<{
    id: string;
    Header: any;
    accessor: string | ((d: any) => any);
    minWidth?: number;
    maxWidth?: number;
    width?: number;
    sortable?: boolean;
    resizable?: boolean;
    Cell?: any;
  }> = [
    {
      id: 'title',
      Header: () => (
        <span>
          <span>Title</span>
          <span>
            {sortedColumnName === 'title' && sortDirection === 'asc' && (
              <i className={`far fa-long-arrow-up ${styles.sortIcon}`}></i>
            )}
            {sortedColumnName === 'title' && sortDirection === 'desc' && (
              <i className={`far fa-long-arrow-down ${styles.sortIcon}`}></i>
            )}
          </span>
        </span>
      ),
      accessor: 'title',
      width: 300,
    },
    {
      id: 'eventLocation',
      Header: 'Location',
      accessor: 'eventLocation',
      width: 200,
    },
    {
      id: 'publishedAt',
      Header: () => (
        <span>
          <span>Published Date</span>
          <span>
            {sortedColumnName === 'publishedAt' && sortDirection === 'asc' && (
              <i className={`far fa-long-arrow-up ${styles.sortIcon}`}></i>
            )}
            {sortedColumnName === 'publishedAt' && sortDirection === 'desc' && (
              <i className={`far fa-long-arrow-down ${styles.sortIcon}`}></i>
            )}
          </span>
        </span>
      ),
      accessor: (row: any) =>
        row.publishedAt ? new Date(row.publishedAt).toLocaleDateString() : '',
      width: 150,
    },
    {
      id: 'isUrgent',
      Header: 'Urgent',
      accessor: (row: any) => (row.isUrgent ? 'Yes' : 'No'),
      width: 80,
    },
    {
      id: 'isDuplicate',
      Header: 'Status',
      accessor: (row: any) => {
        if (row.isDuplicate) {
          return (
            <span style={{ color: '#f2711c' }}>
              <i className="far fa-copy" style={{ marginRight: 5 }} />
              Duplicate
            </span>
          );
        }
        return <span style={{ color: '#21ba45' }}>Original</span>;
      },
      width: 100,
      sortable: false,
    },
    {
      id: 'Actions',
      Header: '',
      accessor: 'Actions',
      Cell: (props: any) => {
        const row = props.original;
        return (
          <span className="event-table-cell-actions">
            <button
              type="button"
              className={styles.actionButton}
              title="Edit"
              aria-label={`Edit ${row.title}`}
              onClick={() => handleEditEvent(row.id)}
            >
              <i className={`far fa-solid fa-pencil`} aria-hidden="true"></i>
            </button>
            <button
              type="button"
              className={`${styles.actionButton} ${styles.deleteButton}`}
              title="Delete"
              aria-label={`Delete ${row.title}`}
              onClick={() => {
                setDeleteEventId(row.id);
                dispatch(
                  openMessageModal(
                    `Are you sure you want to delete this ${contentTypeLabel.toLowerCase()}?`,
                    'warning',
                  ),
                );
                setDeleteModalOpen(true);
              }}
            >
              <i className={`far fa-solid fa-trash`} aria-hidden="true"></i>
            </button>
          </span>
        );
      },
      width: 80,
      sortable: false,
      resizable: false,
    },
  ];

  const fetchData = async (
    pageIndex: number,
    pageSize: number,
    sortedColumnName: string,
    sortDirection: string,
    searchTerm: string,
    type: PostType,
  ) => {
    try {
      dispatch(setLoading(true));
      console.group('[DIAG] EventsList.fetchData');
      console.log('Params:', { type, pageIndex, pageSize, sortedColumnName, sortDirection, searchTerm });
      const paginationData = await eventService.getPosts(
        type,
        pageIndex,
        pageSize,
        sortedColumnName,
        sortDirection,
        searchTerm ? searchTerm.trim() : '',
        true, // includeDuplicates - admin should see all posts
      );
      console.log('Success:', { postCount: paginationData.data.length, totalPages: paginationData.totalPages });
      console.groupEnd();
      dispatch(setEvents(paginationData.data));
      dispatch(setTotalPages(paginationData.totalPages));
    } catch (error) {
      const contentType = type === 'event' ? 'events' : 'articles';
      const err = error as any;
      console.group('[DIAG] EventsList.fetchData ERROR');
      console.error('Error details:', {
        message: err?.message,
        code: err?.code,
        status: err?.response?.status,
        statusText: err?.response?.statusText,
        responseData: err?.response?.data,
        requestUrl: err?.config?.url,
        requestBaseURL: err?.config?.baseURL,
      });
      console.groupEnd();
      console.error(`Error fetching ${contentType}:`, error);
      dispatch(openMessageModal(`Failed to load ${contentType}`, 'error'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Called to populate the events table when the page loads or when postType changes
  useEffect(() => {
    if (isLoadingPageForFirstTime) {
      setIsLoadingPageForFirstTime(false);
      fetchData(pageIndex, pageSize, sortedColumnName, sortDirection, searchTerm, postType);
    } else {
      // Use debounce for search
      const delayInputTimeoutId = setTimeout(() => {
        dispatch(setPageIndex(0));
        fetchData(0, pageSize, sortedColumnName, sortDirection, searchTerm, postType);
      }, 500);
      return () => clearTimeout(delayInputTimeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, postType]);

  return (
    <PanelRow style={{ padding: 0, marginRight: 0, marginLeft: 0 }}>
      <MessageModal
        modalOpen={deleteModalOpen}
        onConfirmationButtonClick={() => {
          handleDeleteEvent(deleteEventId);
        }}
        onCloseButtonClick={() => {
          setDeleteModalOpen(false);
        }}
      />
      <Panel style={{ fontFamily: 'Lato', fontSize: 14 }}>
        <div className={styles.headerContainer}>
          <h1 className={styles.heading}>Content Management</h1>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <Button.Group>
              <Button primary={postType === 'news'} onClick={() => setPostType('news')}>
                Articles
              </Button>
              <Button primary={postType === 'event'} onClick={() => setPostType('event')}>
                Events
              </Button>
            </Button.Group>
            <Button primary onClick={handleCreateEvent}>
              Create {contentTypeLabel}
            </Button>
            <Button
              basic
              color="blue"
              onClick={handleReindex}
              loading={isReindexing}
              disabled={isReindexing}
              title="Rebuild the search index from the database to fix stale data in the mobile app"
            >
              <i className="fas fa-sync-alt" style={{ marginRight: 6 }} />
              Rebuild Search Index
            </Button>
          </div>
        </div>
        <div className={styles.searchContainer}>
          <Input
            icon="search"
            placeholder={`Search ${contentTypeLabelPlural.toLowerCase()}...`}
            value={searchTerm}
            onChange={(e: any) => {
              dispatch(setSearchTerm(e.target.value));
            }}
            fluid
            aria-label="Search content"
          />
        </div>
        <Spinner isLoading={loading} />
        <ReactTable
          manual
          data={events}
          pages={totalPages}
          loading={loading}
          columns={columns}
          page={pageIndex}
          resizable={false}
          pageSize={pageSize}
          pageSizeOptions={[25, 50, 75, 100]}
          onPageChange={(pageIndex) => {
            dispatch(setPageIndex(pageIndex));
            fetchData(pageIndex, pageSize, sortedColumnName, sortDirection, searchTerm, postType);
          }}
          onPageSizeChange={(pageSize, pageIndex) => {
            dispatch(setPageSize(pageSize));
            dispatch(setPageIndex(pageIndex));
            fetchData(pageIndex, pageSize, sortedColumnName, sortDirection, searchTerm, postType);
          }}
          onSortedChange={(sorted: any) => {
            if (sorted.length > 0) {
              const column = sorted[0];
              dispatch(setSortedColumnName(column.id));
              dispatch(setSortDirection(column.desc ? 'desc' : 'asc'));
              fetchData(
                pageIndex,
                pageSize,
                column.id,
                column.desc ? 'desc' : 'asc',
                searchTerm,
                postType,
              );
            }
          }}
          getTdProps={(state: any, rowInfo: any, column: any) => {
            if (rowInfo) {
              return {
                style: {
                  paddingTop: 20,
                  paddingBottom: 20,
                  cursor: column.id !== 'Actions' ? 'pointer' : 'default',
                },
              };
            }
            return {
              style: {
                paddingTop: 20,
                paddingBottom: 20,
              },
            };
          }}
          getTrGroupProps={(state: any, rowInfo: any) => {
            if (rowInfo && rowInfo.index !== pageSize - 1) {
              return {
                style: {
                  borderBottom: 'solid 1px #AAAAAA',
                },
              };
            }
            return {
              style: {},
            };
          }}
        />
      </Panel>
    </PanelRow>
  );
};
